import { ExternalLink, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import type { AccountResource } from "@octopusdeploy/octopus-server-client";
import { AccountType } from "@octopusdeploy/octopus-server-client";
import type React from "react";
import { useEffect, useState } from "react";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import { ExpandableFormSection, Summary, Note } from "~/components/form";
import { BoundAccountSelect } from "~/components/form/AccountSelect/AccountSelect";
const workerCredentialType = "worker" as const;
const accountCredentialType = "account" as const;
type CredentialsType = typeof workerCredentialType | typeof accountCredentialType;
export interface AwsAuthenticationSectionProperties {
    "Octopus.Action.AwsAccount.UseInstanceRole": string;
    "Octopus.Action.AwsAccount.Variable": string;
}
type AwsAuthenticationSectionProps = ActionEditProps<AwsAuthenticationSectionProperties>;
export const AwsAuthenticationSection: React.FC<AwsAuthenticationSectionProps> = (props) => {
    const { inputDependencies, properties, setProperties, expandedByDefault, localNames, getFieldError } = props;
    const [accounts, setAccounts] = useState<AccountResource[]>([]);
    const [credentialsType, setCredentialsType] = useState<CredentialsType>(workerCredentialType);
    useEffect(() => {
        if (inputDependencies?.accounts) {
            setAccounts(inputDependencies.accounts);
        }
        if (properties["Octopus.Action.AwsAccount.UseInstanceRole"] === "False") {
            setCredentialsType(accountCredentialType);
        }
    }, [inputDependencies, properties]);
    const handleCredentialsChange = (newType: CredentialsType) => {
        if (newType === workerCredentialType) {
            setProperties({
                ["Octopus.Action.AwsAccount.UseInstanceRole"]: "True",
                ["Octopus.Action.AwsAccount.Variable"]: "",
            });
        }
        else {
            setProperties({
                ["Octopus.Action.AwsAccount.UseInstanceRole"]: "False",
                ["Octopus.Action.AwsAccount.Variable"]: "",
            });
        }
        setCredentialsType(newType);
    };
    const getCredentialsSummary = () => {
        if (credentialsType === workerCredentialType) {
            return Summary.summary("Use credentials configured on the worker");
        }
        else if (credentialsType === accountCredentialType) {
            return Summary.summary("Use an AWS account");
        }
    };
    const getAccountSummary = () => {
        const accountVariable = properties["Octopus.Action.AwsAccount.Variable"];
        return accountVariable ? Summary.summary(`AWS Account: ${accountVariable}`) : Summary.placeholder("No account selected");
    };
    return (<>
            <ExpandableFormSection errorKey="Octopus.Action.AwsAccount.UseInstanceRole" isExpandedByDefault={expandedByDefault} title="Credentials" help="Execute using credentials configured on the worker or using a specific account." summary={getCredentialsSummary()}>
                <RadioButtonGroup value={credentialsType} onChange={handleCredentialsChange} title="Credentials">
                    <RadioButton value={workerCredentialType} label="Use credentials configured on the worker"/>
                    <RadioButton value={accountCredentialType} label="Use AWS account"/>
                </RadioButtonGroup>
                <Note>
                    AWS supports several different ways of supplying credentials on a worker to access services with. For more information, see <ExternalLink href={"AWSAuthenticationCredentials"}>Setting credentials</ExternalLink>
                </Note>
            </ExpandableFormSection>

            {credentialsType !== workerCredentialType && (<ExpandableFormSection errorKey="Octopus.Action.AwsAccount.Variable" isExpandedByDefault={expandedByDefault} title="AWS Account" help={`The Account to authenticate with.`} summary={getAccountSummary()}>
                    <BoundAccountSelect variableLookup={{
                localNames,
            }} resetValue="" label="AWS Account" value={properties["Octopus.Action.AwsAccount.Variable"] || ""} type={[AccountType.AmazonWebServicesAccount, AccountType.AmazonWebServicesOidcAccount]} allowClear={true} onChange={(val: string) => setProperties({ ["Octopus.Action.AwsAccount.Variable"]: val })} error={getFieldError("Octopus.Action.AwsAccount.Variable")} items={accounts} onRequestRefresh={function (): Promise<never> {
                throw new Error("Function not implemented.");
            }}/>
                </ExpandableFormSection>)}
        </>);
};
AwsAuthenticationSection.displayName = "AwsAuthenticationSection"
